import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { PickupSlot } from '@/types';
import { getWouldPickSlot } from './autoSelectPreview';
import SlotToggleRow from './SlotToggleRow';
import { formatSlotWindow12 } from '@/utils/helpers';

interface AutoSelectToggleRowProps {
  slots: PickupSlot[];
  checked: boolean;
  onChange: (checked: boolean) => void;
  // v2-task-P2 — the per-pickup "change slot" edit modal reuses this row to
  // govern the STANDING auto-select preference, which is meaningless without
  // a preferred slot to advance FROM. When disabled, the Switch is inert and
  // the normal description/preview are replaced by a muted explanatory hint.
  disabled?: boolean;
  // Forwarded to SlotToggleRow — false when rendered inside SlotModal's merged
  // two-toggle card (no own border/radius).
  bordered?: boolean;
  // Forwarded to SlotToggleRow — compact one-line layout inside the automation strip.
  compact?: boolean;
}

// v2-task-9 (spec §4.1) — SlotModal's preference-mode-only auto-select
// toggle + live preview. Placement: between the modal's subtitle and the
// slot list (see SlotModal.tsx). Styling reuses the existing design system
// verbatim via the shared SlotToggleRow (v2-task-10fe extraction, §4.4):
// `radius-md` (rounded-xl, 12px) bordered row — NOT radius-lg, which is
// reserved for outer shells — and the §2.5 selected-state tint
// (rgba(237,224,74,0.12)) as a STATIC, non-interactive preview chip (no
// box-shadow ring, since it isn't a button). The no-eligible-slot fallback
// reuses the exact amber-50/700 + AlertTriangle construction already
// established at SlotPicker.tsx:169 / CurrentSlotBanner.tsx:136-138.
const AutoSelectToggleRow = ({
  slots,
  checked,
  onChange,
  disabled = false,
  bordered = true,
  compact = false,
}: AutoSelectToggleRowProps) => {
  const { t } = useTranslation();
  const wouldPickSlot = checked && !disabled ? getWouldPickSlot(slots) : null;

  const extra = wouldPickSlot ? (
    // Hugs its content (w-fit) with a leading confirmation glyph, so it reads as
    // an intentional "this is what we'd book" chip rather than a full-width bar.
    <div
      className='mt-2.5 inline-flex w-fit max-w-full items-center gap-2 rounded-xl px-3 py-1.5'
      style={{ background: 'rgba(237,224,74,0.12)' }}
    >
      <CheckCircle2 size={14} color='rgb(57,57,60)' className='shrink-0' />
      <span className='text-xs font-medium text-[rgb(115,123,140)]'>
        {String(t('auto_select_preview_prefix'))}
      </span>
      <span className='text-sm font-bold text-[rgb(57,57,60)]'>
        <bdi>
          {formatSlotWindow12(wouldPickSlot.window_from, wouldPickSlot.window_to)}
        </bdi>
      </span>
      <span className='text-xs font-medium text-[rgb(115,123,140)]'>· {wouldPickSlot.label}</span>
    </div>
  ) : (
    <div className='mt-2.5 flex items-start gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-xs font-medium leading-5 text-amber-700'>
      <span className='flex h-5 shrink-0 items-center'>
        <AlertTriangle size={12} />
      </span>
      <span>{String(t('auto_select_no_slot_available'))}</span>
    </div>
  );

  return (
    <SlotToggleRow
      label={String(t('auto_select_toggle_label'))}
      description={String(
        t(compact ? 'auto_select_toggle_helper_short' : 'auto_select_toggle_description'),
      )}
      disabledHint={String(t('auto_select_toggle_disabled_hint'))}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      extra={extra}
      bordered={bordered}
      compact={compact}
    />
  );
};

export default AutoSelectToggleRow;
